/**
 * Overland位置データ地図コンポーネント
 *
 * 訪問場所（マーカー）と移動経路（ポリライン）を表示
 * 連続した移動をセグメントに分割して表示（GPSが途切れた箇所は直線で繋がない）
 */

import { useMap } from '@vis.gl/react-google-maps'
import { Bike, Car, Footprints, MapPinPlus, X } from 'lucide-react'
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { updateLocationsMotion } from '@/api/services/overland'
import type { MovementSession, OverlandLocation, OverlandVisit } from '@/api/types/overland'
import { Button } from '@/components/ui/button'
import { formatTimeJST, getMotionConfig } from '@/lib/activity-format-utils'
import { calculateDistance } from '@/lib/geo-utils'
import { getPlaceName } from '@/lib/place-utils'
import { cn } from '@/lib/utils'

import { GoogleCircleMarker } from './google-circle-marker'
import { GoogleMapCanvas } from './google-map-canvas'
import { DEFAULT_MAP_CENTER } from './google-maps-config'
import { GooglePolyline } from './google-polyline'
import { MapTooltip } from './map-tooltip'
import { useFitBounds } from './use-fit-bounds'

// セグメント分割の閾値
const MAX_TIME_GAP_MS = 3 * 60 * 1000 // 3分以上の間隔で分割
const MAX_DISTANCE_KM = 0.5 // 500m以上離れたら分割

// Visitマーカーを移動経路より前面に出す（重なった位置でもマーカー側がホバー・クリックを取れるようにする）
const ROUTE_Z_INDEX = 1
const VISIT_MARKER_Z_INDEX = 100

// Visitマーカーの見た目（ピクセル）。ポリライン上のヒット判定にも使う
const VISIT_MARKER_RADIUS_PX = 10
const VISIT_MARKER_BORDER_PX = 2
const VISIT_HIT_RADIUS_PX = VISIT_MARKER_RADIUS_PX + VISIT_MARKER_BORDER_PX

// RouteSegment型（motionと時刻を含む）
type RoutePoint = {
  lat: number
  lng: number
  timestamp: string
}

type RouteSegment = {
  path: google.maps.LatLngLiteral[]
  pointsWithTime: RoutePoint[] // ツールチップ用
  motion: string
  startTime: string
  endTime: string
}

/** ホバー中に表示するツールチップの内容 */
type HoverInfo = {
  position: google.maps.LatLngLiteral
  title: string
  subtitle: string
  hint: string
}

/** 指定座標に最も近いポイントを返す */
const findClosestPoint = (points: RoutePoint[], target: google.maps.LatLngLiteral): RoutePoint | null => {
  let closest: RoutePoint | null = null
  let minDistance = Infinity

  for (const point of points) {
    const distance = (point.lat - target.lat) ** 2 + (point.lng - target.lng) ** 2
    if (distance < minDistance) {
      minDistance = distance
      closest = point
    }
  }

  return closest
}

type Pixel = { x: number; y: number }

/** 緯度経度を地図コンテナ左上基準のピクセル座標に変換する */
const toContainerPixel = (map: google.maps.Map, latLng: google.maps.LatLngLiteral): Pixel | null => {
  const projection = map.getProjection()
  const zoom = map.getZoom()
  const center = map.getCenter()
  if (!projection || zoom === undefined || !center) return null

  const centerPoint = projection.fromLatLngToPoint(center)
  const point = projection.fromLatLngToPoint(latLng)
  if (!centerPoint || !point) return null

  const scale = 2 ** zoom
  const div = map.getDiv()
  return {
    x: (point.x - centerPoint.x) * scale + div.clientWidth / 2,
    y: (point.y - centerPoint.y) * scale + div.clientHeight / 2,
  }
}

/** 指定ピクセルがVisitマーカーの円内に入っていれば、最も近いVisitを返す */
const findVisitAtPixel = (map: google.maps.Map, visits: OverlandVisit[], target: Pixel): OverlandVisit | null => {
  let closest: OverlandVisit | null = null
  let minDistancePx = VISIT_HIT_RADIUS_PX

  for (const visit of visits) {
    const pixel = toContainerPixel(map, { lat: visit.latitude, lng: visit.longitude })
    if (!pixel) continue
    const distancePx = Math.hypot(pixel.x - target.x, pixel.y - target.y)
    if (distancePx <= minDistancePx) {
      minDistancePx = distancePx
      closest = visit
    }
  }

  return closest
}

/**
 * 指定座標が画面上でVisitマーカーの円内に入っていれば、最も近いVisitを返す
 *
 * ポリラインのイベントはGoogle Maps側のヒット判定でマーカーDOMより先に取られることがあるため、
 * ポリラインのクリック時にピクセル距離を計算してVisitを優先させる。
 */
const findVisitAtLatLng = (
  map: google.maps.Map | null,
  visits: OverlandVisit[],
  target: google.maps.LatLngLiteral,
): OverlandVisit | null => {
  if (!map) return null
  const targetPixel = toContainerPixel(map, target)
  if (!targetPixel) return null
  return findVisitAtPixel(map, visits, targetPixel)
}

const buildVisitHoverInfo = (visit: OverlandVisit): HoverInfo => ({
  position: { lat: visit.latitude, lng: visit.longitude },
  title: getPlaceName(visit),
  subtitle: formatTimeJST(visit.timestamp),
  hint: 'タップで詳細',
})

/** 地図インスタンスを親コンポーネントの ref に渡す（Map の内側でしか useMap が使えないため） */
function MapInstanceCapture({ mapRef }: { mapRef: RefObject<google.maps.Map | null> }) {
  const map = useMap()
  useEffect(() => {
    mapRef.current = map
  }, [map, mapRef])
  return null
}

/**
 * カーソルがVisitマーカーの円内にあるかを監視し、変化したときだけ通知する
 *
 * マーカーDOMの mouseenter/mouseleave とポリラインのイベントはヒット判定を奪い合って
 * 高速に切り替わる（ツールチップがちらつく）ため、地図要素の pointermove をキャプチャフェーズで受けて
 * 円の内外をピクセル距離で判定する。円内にいる間は同じVisitが選ばれ続ける。
 */
function VisitHoverTracker({
  visits,
  onChange,
}: {
  visits: OverlandVisit[]
  onChange: (visit: OverlandVisit | null) => void
}) {
  const map = useMap()
  const visitsRef = useRef(visits)
  const onChangeRef = useRef(onChange)
  const evaluateRef = useRef<(() => void) | null>(null)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])
  // Visitの編集・削除時もカーソルを動かさずにツールチップを最新化する
  useEffect(() => {
    visitsRef.current = visits
    evaluateRef.current?.()
  }, [visits])

  useEffect(() => {
    if (!map) return

    const div = map.getDiv()
    // 再取得で中身が変わったVisitも通知できるよう、IDではなくオブジェクト参照で比較する
    let currentVisit: OverlandVisit | null = null
    let lastPointer: Pixel | null = null
    const update = (visit: OverlandVisit | null) => {
      if (visit === currentVisit) return
      currentVisit = visit
      onChangeRef.current(visit)
    }
    const evaluate = () => update(lastPointer ? findVisitAtPixel(map, visitsRef.current, lastPointer) : null)
    evaluateRef.current = evaluate
    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return
      const rect = div.getBoundingClientRect()
      lastPointer = { x: event.clientX - rect.left, y: event.clientY - rect.top }
      evaluate()
    }
    const handlePointerLeave = () => {
      lastPointer = null
      update(null)
    }

    div.addEventListener('pointermove', handlePointerMove, true)
    div.addEventListener('pointerleave', handlePointerLeave)
    // ホイールズームなどカーソルを動かさずに地図が動いた場合も判定し直す
    const boundsListener = map.addListener('bounds_changed', evaluate)
    return () => {
      div.removeEventListener('pointermove', handlePointerMove, true)
      div.removeEventListener('pointerleave', handlePointerLeave)
      boundsListener.remove()
      evaluateRef.current = null
      update(null)
    }
  }, [map])

  return null
}

/**
 * 位置データのmotion配列から主要なmotionを取得
 * 優先順位: walking > running > cycling > driving > stationary
 */
const getPrimaryMotion = (motions: string[]): string => {
  const priority = ['walking', 'running', 'cycling', 'driving', 'stationary']
  for (const p of priority) {
    if (motions.includes(p)) return p
  }
  return motions[0] ?? 'unknown'
}

/**
 * motion未設定のポイントに前後のmotionを継承させる
 */
const fillMissingMotion = (locations: OverlandLocation[]): (OverlandLocation & { resolvedMotion: string })[] => {
  const result: (OverlandLocation & { resolvedMotion: string })[] = []

  // 最初のパス: 各ポイントの主要motionを決定
  const motions: string[] = locations.map((loc) => {
    if (loc.motion && loc.motion.length > 0) {
      return getPrimaryMotion(loc.motion)
    }
    return ''
  })

  // 前方から継承
  let lastKnownMotion = 'unknown'
  for (let i = 0; i < motions.length; i++) {
    const currentMotion = motions[i]
    if (currentMotion) {
      lastKnownMotion = currentMotion
    } else {
      motions.splice(i, 1, lastKnownMotion)
    }
  }

  // 後方から継承（前方で埋まらなかった場合）
  lastKnownMotion = 'unknown'
  for (let i = motions.length - 1; i >= 0; i--) {
    const currentMotion = motions[i]
    if (currentMotion && currentMotion !== 'unknown') {
      lastKnownMotion = currentMotion
    } else if (currentMotion === 'unknown') {
      motions.splice(i, 1, lastKnownMotion)
    }
  }

  // 結果を構築
  for (let i = 0; i < locations.length; i++) {
    const loc = locations[i]!
    result.push({
      ...loc,
      resolvedMotion: motions[i] ?? 'unknown',
    })
  }

  return result
}

type OverlandMapProps = {
  locations: OverlandLocation[]
  visits: OverlandVisit[]
  height?: string
  /** モーション更新完了時のコールバック */
  onMotionUpdated?: () => void
  /** 訪問クリック時のコールバック */
  onVisitClick?: (visit: OverlandVisit) => void
  /** Visit作成リクエストのコールバック（ポリラインクリックから） */
  onCreateVisit?: (lat: number, lng: number, timestamp: string) => void
  /** ハイライト対象の移動セッション（タイムラインからの連動用） */
  highlightedSession?: MovementSession | null
  /**
   * fitBounds のトリガーキー。値が変わったときのみ再フィットする。
   * 未指定の場合は初回に有効データが揃った時のみフィット。
   * Visit編集など同一データセット内の更新でユーザーの視点を維持したい場合、
   * 日付など「本当に切り替わったタイミング」を渡すこと。
   */
  fitKey?: string | number
}

// 地図の表示範囲を自動調整するコンポーネント
function MapBoundsUpdater({
  locations,
  visits,
  fitKey,
}: {
  locations: OverlandLocation[]
  visits: OverlandVisit[]
  fitKey?: string | number
}) {
  const points = useMemo(
    () => [
      ...locations.map((loc) => ({ lat: loc.latitude, lng: loc.longitude })),
      ...visits.map((visit) => ({ lat: visit.latitude, lng: visit.longitude })),
    ],
    [locations, visits],
  )

  useFitBounds({ points, padding: 20, maxZoom: 16, fitKey })

  return null
}

/**
 * ハイライトされたセッションの範囲にマップをズーム
 */
function HighlightBoundsUpdater({ session }: { session: MovementSession }) {
  const points = useMemo(() => session.locations.map((loc) => ({ lat: loc.latitude, lng: loc.longitude })), [session])

  useFitBounds({ points, padding: 40, maxZoom: 16, fitKey: `${session.startTime}-${session.endTime}` })

  return null
}

export function OverlandMap({
  locations,
  visits,
  height = '200px',
  onMotionUpdated,
  onVisitClick,
  onCreateVisit,
  highlightedSession,
  fitKey,
}: OverlandMapProps) {
  // 選択中のセグメント
  const [selectedSegment, setSelectedSegment] = useState<RouteSegment | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  // ホバー中のツールチップ（JSX で read するため state 、高頻度の mousemove は pending ref + 50ms スロットルで反映）
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null)
  const pendingHoverRef = useRef<HoverInfo | null>(null)
  // Visitマーカー（青い点）の円内にカーソルがある間は移動経路より優先し、ツールチップを維持する
  const activeVisitRef = useRef<OverlandVisit | null>(null)
  // ポリラインイベント内でVisitのヒット判定をするための地図インスタンス
  const mapRef = useRef<google.maps.Map | null>(null)
  // mousemoveのスロットリング用
  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleTooltipUpdate = useCallback(() => {
    if (throttleTimerRef.current) return
    throttleTimerRef.current = setTimeout(() => {
      throttleTimerRef.current = null
      if (activeVisitRef.current) return
      setHoverInfo(pendingHoverRef.current)
    }, 50)
  }, [])
  const clearHover = useCallback(() => {
    pendingHoverRef.current = null
    setHoverInfo(null)
  }, [])
  // Visitのツールチップはスロットルを挟まず即時反映する
  const handleVisitHoverChange = useCallback(
    (visit: OverlandVisit | null) => {
      activeVisitRef.current = visit
      if (!visit) {
        clearHover()
        return
      }
      const info = buildVisitHoverInfo(visit)
      pendingHoverRef.current = info
      setHoverInfo(info)
    },
    [clearHover],
  )
  useEffect(() => {
    return () => {
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current)
        throttleTimerRef.current = null
      }
    }
  }, [])
  // クリック地点の情報（Visit作成用）
  const [clickedPoint, setClickedPoint] = useState<RoutePoint | null>(null)

  // セグメント選択解除
  const clearSelection = useCallback(() => {
    setSelectedSegment(null)
    setClickedPoint(null)
  }, [])

  // セグメントがハイライトされたセッションに属するか判定
  const isSegmentHighlighted = useCallback(
    (segment: RouteSegment): boolean => {
      if (!highlightedSession) return false
      const sessionStart = new Date(highlightedSession.startTime).getTime()
      const sessionEnd = new Date(highlightedSession.endTime).getTime()
      const segStart = new Date(segment.startTime).getTime()
      const segEnd = new Date(segment.endTime).getTime()
      // セグメントの時間範囲がセッションの時間範囲と重複していればハイライト
      return segStart < sessionEnd && segEnd > sessionStart
    },
    [highlightedSession],
  )

  // セグメントのopacityを決定
  const getSegmentOpacity = useCallback(
    (segment: RouteSegment): number => {
      if (selectedSegment === segment) return 0.3
      if (highlightedSession) {
        return isSegmentHighlighted(segment) ? 0.9 : 0.15
      }
      return 0.8
    },
    [selectedSegment, highlightedSession, isSegmentHighlighted],
  )

  // モーション変更ハンドラ
  const handleMotionChange = useCallback(
    async (newMotion: string) => {
      if (!selectedSegment) return

      setIsUpdating(true)
      try {
        await updateLocationsMotion(selectedSegment.startTime, selectedSegment.endTime, newMotion)
        setSelectedSegment(null)
        onMotionUpdated?.()
      } catch {
        // エラーはUI上で通知する手段がないため無視（APIエラーはaxiosインターセプターで処理）
      } finally {
        setIsUpdating(false)
      }
    },
    [selectedSegment, onMotionUpdated],
  )

  // 初期中心を計算
  const center = useMemo((): google.maps.LatLngLiteral => {
    const firstVisit = visits[0]
    if (firstVisit) return { lat: firstVisit.latitude, lng: firstVisit.longitude }

    const firstLocation = locations[0]
    if (firstLocation) return { lat: firstLocation.latitude, lng: firstLocation.longitude }

    return DEFAULT_MAP_CENTER
  }, [visits, locations])

  // 全位置データから移動経路のポリラインセグメントを生成
  // 時間間隔、距離、motionの変化で別セグメントに分割
  const routeSegments = useMemo((): RouteSegment[] => {
    if (locations.length < 2) return []

    // 時間順にソート（古い順）
    const sortedLocations = [...locations].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    )

    // motion未設定のポイントに前後のmotionを継承
    const locationsWithMotion = fillMissingMotion(sortedLocations)

    const segments: RouteSegment[] = []
    let currentPath: google.maps.LatLngLiteral[] = []
    let currentPointsWithTime: RoutePoint[] = []
    let currentMotion = 'unknown'
    let segmentStartTime = ''
    let segmentEndTime = ''

    for (let i = 0; i < locationsWithMotion.length; i++) {
      const loc = locationsWithMotion[i]
      if (!loc) continue

      const point: google.maps.LatLngLiteral = { lat: loc.latitude, lng: loc.longitude }
      const pointWithTime: RoutePoint = { lat: loc.latitude, lng: loc.longitude, timestamp: loc.timestamp }
      const motion = loc.resolvedMotion

      if (currentPath.length === 0) {
        currentPath.push(point)
        currentPointsWithTime.push(pointWithTime)
        currentMotion = motion
        segmentStartTime = loc.timestamp
        segmentEndTime = loc.timestamp
        continue
      }

      const prevLoc = locationsWithMotion[i - 1]
      if (!prevLoc) {
        currentPath.push(point)
        currentPointsWithTime.push(pointWithTime)
        continue
      }

      // 時間差と距離をチェック
      const timeGap = new Date(loc.timestamp).getTime() - new Date(prevLoc.timestamp).getTime()
      const distance = calculateDistance(prevLoc.latitude, prevLoc.longitude, loc.latitude, loc.longitude)

      // 閾値を超えたら、またはmotionが変わったら新しいセグメントを開始
      const motionChanged = motion !== currentMotion
      if (timeGap > MAX_TIME_GAP_MS || distance > MAX_DISTANCE_KM || motionChanged) {
        // motionが変わった場合は、現在のポイントを前のセグメントの終点にも追加（線を繋げる）
        if (motionChanged && currentPath.length >= 1) {
          currentPath.push(point)
          currentPointsWithTime.push(pointWithTime)
          segmentEndTime = loc.timestamp
        }
        // 現在のセグメントが2点以上あれば追加
        if (currentPath.length >= 2) {
          segments.push({
            path: currentPath,
            pointsWithTime: currentPointsWithTime,
            motion: currentMotion,
            startTime: segmentStartTime,
            endTime: segmentEndTime,
          })
        }
        currentPath = [point]
        currentPointsWithTime = [pointWithTime]
        currentMotion = motion
        segmentStartTime = loc.timestamp
        segmentEndTime = loc.timestamp
      } else {
        currentPath.push(point)
        currentPointsWithTime.push(pointWithTime)
        segmentEndTime = loc.timestamp
      }
    }

    // 最後のセグメントを追加
    if (currentPath.length >= 2) {
      segments.push({
        path: currentPath,
        pointsWithTime: currentPointsWithTime,
        motion: currentMotion,
        startTime: segmentStartTime,
        endTime: segmentEndTime,
      })
    }

    return segments
  }, [locations])

  return (
    <div className="rounded-lg overflow-hidden border relative z-0" style={{ height }}>
      <GoogleMapCanvas defaultCenter={center} defaultZoom={14} className="h-full w-full" mapTypeControl>
        <MapInstanceCapture mapRef={mapRef} />
        <VisitHoverTracker visits={visits} onChange={handleVisitHoverChange} />
        {/* 移動経路のポリライン（セグメントごとに表示、motion別に色分け） */}
        {routeSegments.map((segment, index) => (
          <GooglePolyline
            key={`route-${index}`}
            path={segment.path}
            strokeColor={getMotionConfig(segment.motion).hexColor}
            strokeWeight={highlightedSession && isSegmentHighlighted(segment) ? 8 : 6}
            strokeOpacity={getSegmentOpacity(segment)}
            zIndex={ROUTE_Z_INDEX}
            onClick={(latLng) => {
              // Visitの円内のクリックはVisitを優先する
              const visit = findVisitAtLatLng(mapRef.current, visits, latLng)
              if (visit) {
                setSelectedSegment(null)
                onVisitClick?.(visit)
                return
              }
              setSelectedSegment(segment)
              // クリック地点に最も近いポイントを記録（Visit作成用）
              setClickedPoint(findClosestPoint(segment.pointsWithTime, latLng))
            }}
            onMouseMove={(latLng) => {
              if (activeVisitRef.current) return
              const closest = findClosestPoint(segment.pointsWithTime, latLng)
              pendingHoverRef.current = {
                position: latLng,
                title: getMotionConfig(segment.motion).label,
                subtitle: closest
                  ? formatTimeJST(closest.timestamp)
                  : `${formatTimeJST(segment.startTime)} - ${formatTimeJST(segment.endTime)}`,
                hint: 'タップで移動手段変更 / Visit追加',
              }
              scheduleTooltipUpdate()
            }}
            onMouseOut={() => {
              if (activeVisitRef.current) return
              clearHover()
            }}
          />
        ))}

        {/* 選択中セグメントのハイライト */}
        {selectedSegment && (
          <GooglePolyline
            path={selectedSegment.path}
            strokeColor="#FACC15" // yellow-400
            strokeWeight={10}
            dashed
            zIndex={ROUTE_Z_INDEX}
            clickable={false}
          />
        )}

        {/* 訪問場所のマーカー */}
        {visits.map((visit) => (
          <GoogleCircleMarker
            key={`visit-${visit.id}`}
            position={{ lat: visit.latitude, lng: visit.longitude }}
            radius={VISIT_MARKER_RADIUS_PX}
            color="#6366F1" // indigo-500
            fillColor="#818CF8" // indigo-400
            fillOpacity={0.8}
            weight={VISIT_MARKER_BORDER_PX}
            zIndex={VISIT_MARKER_Z_INDEX}
            onClick={() => {
              setSelectedSegment(null)
              onVisitClick?.(visit)
            }}
          />
        ))}

        {/* カーソル追従ツールチップ */}
        <MapTooltip position={hoverInfo?.position ?? null}>
          <div className="font-medium">{hoverInfo?.title}</div>
          <div className="text-gray-500">{hoverInfo?.subtitle}</div>
          <div className="text-blue-500 mt-1">{hoverInfo?.hint}</div>
        </MapTooltip>

        {/* 地図の表示範囲を自動調整（fitKey が変わったときのみ再フィット） */}
        <MapBoundsUpdater locations={locations} visits={visits} fitKey={fitKey} />

        {/* ハイライトされたセッションの範囲にズーム */}
        {highlightedSession && <HighlightBoundsUpdater session={highlightedSession} />}
      </GoogleMapCanvas>

      {/* 凡例 */}
      {routeSegments.length > 0 && (
        <div className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm rounded-lg shadow-md p-2 z-1000 text-xs">
          <div className="font-medium mb-1 text-gray-700">移動手段</div>
          {(['walking', 'running', 'driving', 'cycling', 'stationary'] as const).map((motion) => {
            const config = getMotionConfig(motion)
            return (
              <div key={motion} className="flex items-center gap-1.5">
                <div className="w-3 h-1 rounded-full" style={{ backgroundColor: config.hexColor }} />
                <span className="text-gray-600">{config.label}</span>
              </div>
            )
          })}
        </div>
      )}

      {/* モーション変更 & Visit追加 UI */}
      {selectedSegment && (
        <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t shadow-lg z-1000 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium">
              移動手段を変更
              <span className="text-gray-500 font-normal ml-2">
                {formatTimeJST(selectedSegment.startTime)} - {formatTimeJST(selectedSegment.endTime)}
              </span>
            </div>
            <Button variant="ghost" size="icon" className="size-7" onClick={clearSelection} disabled={isUpdating}>
              <X className="size-4" />
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleMotionChange('walking')}
              disabled={isUpdating || selectedSegment.motion === 'walking'}
              className={cn('gap-1.5', selectedSegment.motion === 'walking' && 'border-green-500 bg-green-50')}
            >
              <Footprints className="size-4" style={{ color: getMotionConfig('walking').hexColor }} />
              徒歩
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleMotionChange('cycling')}
              disabled={isUpdating || selectedSegment.motion === 'cycling'}
              className={cn('gap-1.5', selectedSegment.motion === 'cycling' && 'border-amber-500 bg-amber-50')}
            >
              <Bike className="size-4" style={{ color: getMotionConfig('cycling').hexColor }} />
              自転車
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleMotionChange('driving')}
              disabled={isUpdating || selectedSegment.motion === 'driving'}
              className={cn('gap-1.5', selectedSegment.motion === 'driving' && 'border-blue-500 bg-blue-50')}
            >
              <Car className="size-4" style={{ color: getMotionConfig('driving').hexColor }} />
              車/電車
            </Button>

            {/* Visit追加ボタン（クリック地点が取得できている場合のみ表示） */}
            {onCreateVisit && clickedPoint && (
              <>
                <div className="w-px h-6 bg-border self-center mx-1" />
                <Button
                  variant="default"
                  size="sm"
                  className="gap-1.5 bg-indigo-500 hover:bg-indigo-600"
                  onClick={() => {
                    onCreateVisit(clickedPoint.lat, clickedPoint.lng, clickedPoint.timestamp)
                    clearSelection()
                  }}
                  disabled={isUpdating}
                >
                  <MapPinPlus className="size-4" />
                  ここにVisitを追加
                  <span className="text-xs opacity-75">({formatTimeJST(clickedPoint.timestamp)})</span>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
